import {
  AccessTime,
  Build,
  Group,
  Person,
  Circle,
} from "@mui/icons-material";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState } from "react";
import { Work } from "../../../store/types/work.types";
import AssignWorkDialog from "../../dialogs/AssignWorkDialog";

interface WorkInfoBarProps {
  work: Work;
}

const getStateChip = (state: number) => {
  switch (state) {
    case 1:
      return { label: "Aktiv", color: "#4caf50" };
    case 2:
      return { label: "Stängd", color: "#9e9e9e" };
    case 3:
      return { label: "Utkast", color: "#ff9800" };
    case 4:
      return { label: "Löst", color: "#2196f3" };
    default:
      return { label: "Okänd", color: "#9e9e9e" };
  }
};

const formatDateShort = (dateString: string) => {
  return format(new Date(dateString), "d MMM yyyy", { locale: sv });
};

const formatDateFull = (dateString: string) => {
  return format(new Date(dateString), "d MMM yyyy HH:mm", { locale: sv });
};

const WorkInfoBar = ({ work }: WorkInfoBarProps) => {
  const [assignOpen, setAssignOpen] = useState(false);
  const state = getStateChip(work.State);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Chip
          icon={<Circle sx={{ fontSize: "10px !important", color: `${state.color} !important` }} />}
          label={state.label}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            borderColor: state.color,
            color: state.color,
          }}
        />

        <Tooltip
          title={
            <>
              Skapad: {formatDateFull(work.Created)}
              <br />
              Uppdaterad: {formatDateFull(work.Modified)}
            </>
          }
          arrow
        >
          <Chip
            icon={<AccessTime sx={{ fontSize: "14px !important" }} />}
            label={formatDateShort(work.Created)}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.75rem", cursor: "default" }}
          />
        </Tooltip>

        <Chip
          icon={<Build sx={{ fontSize: "14px !important" }} />}
          label={work.CreatedBy.Name}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />

        <Tooltip title="Klicka för att ändra tilldelning" arrow>
          <Chip
            icon={<Person sx={{ fontSize: "14px !important" }} />}
            label={work.AssignedTo?.Name || "Ej tilldelad"}
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => setAssignOpen(true)}
            sx={{ fontSize: "0.75rem", cursor: "pointer" }}
          />
        </Tooltip>

        <Chip
          icon={<Group sx={{ fontSize: "14px !important" }} />}
          label={work.Team.Name}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />

        {work.Description && (
          <Tooltip title={work.Description} arrow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                ml: "auto",
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "default",
              }}
            >
              {work.Description}
            </Typography>
          </Tooltip>
        )}
      </Box>

      <AssignWorkDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        work={work}
      />
    </>
  );
};

export default WorkInfoBar;
